import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

// ─────────────────────────────────────────────────────────────
// Résolution des permissions « plateforme » de l'utilisateur connecté.
//   • typecompte 0 (superadmin principal) → accès complet.
//   • typecompte 1 SANS ligne platform_team → accès complet (superadmins
//     historiques).
//   • typecompte 1 AVEC ligne platform_team → accès limité à ses permissions.
// ─────────────────────────────────────────────────────────────

export interface PlatformPermissionSnapshot {
  isMaster: boolean;
  isStaff: boolean;
  permissions: string[];
  roleLabel: string;
  status: string;
}

const MASTER: PlatformPermissionSnapshot = {
  isMaster: true,
  isStaff: false,
  permissions: ['*'],
  roleLabel: 'Super Admin',
  status: 'active',
};

const cache = new Map<number, PlatformPermissionSnapshot>();
const inflight = new Map<number, Promise<PlatformPermissionSnapshot>>();

/** Vide le cache (après modif des permissions d'un travailleur). */
export function invalidatePlatformPermissions(userId?: number): void {
  if (userId == null) cache.clear();
  else cache.delete(userId);
}

async function resolvePlatformPermissions(
  userId: number,
  typecompte: number,
): Promise<PlatformPermissionSnapshot> {
  if (typecompte === 0) return MASTER;
  try {
    const { data } = await supabase
      .from('platform_team')
      .select('role_label, permissions, status')
      .eq('user_id', userId)
      .maybeSingle();

    // Aucune ligne : superadmin historique → accès complet.
    if (!data) return MASTER;

    const row = data as { role_label: string | null; permissions: unknown; status: string | null };
    const status = row.status || 'active';
    const perms = Array.isArray(row.permissions) ? (row.permissions as string[]) : [];
    if (status !== 'active') {
      return { isMaster: false, isStaff: true, permissions: [], roleLabel: row.role_label || 'Suspendu', status };
    }
    if (perms.includes('*')) {
      return { isMaster: true, isStaff: true, permissions: ['*'], roleLabel: row.role_label || 'Super Admin', status };
    }
    return { isMaster: false, isStaff: true, permissions: perms, roleLabel: row.role_label || 'Agent plateforme', status };
  } catch {
    // En cas d'erreur réseau, on n'enferme pas le superadmin dehors.
    return MASTER;
  }
}

function loadPlatformPermissions(userId: number, typecompte: number): Promise<PlatformPermissionSnapshot> {
  const cached = cache.get(userId);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(userId);
  if (pending) return pending;
  const promise = resolvePlatformPermissions(userId, typecompte)
    .then((snap) => { cache.set(userId, snap); inflight.delete(userId); return snap; })
    .catch(() => { inflight.delete(userId); return MASTER; });
  inflight.set(userId, promise);
  return promise;
}

export interface PlatformPermissionsApi {
  loading: boolean;
  ready: boolean;
  isMaster: boolean;
  isStaff: boolean;
  roleLabel: string;
  permissions: string[];
  has: (permission: string | string[] | null | undefined) => boolean;
  refresh: () => void;
}

export function usePlatformPermissions(): PlatformPermissionsApi {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const typecompte = user?.typecompte ?? 0;
  const [snapshot, setSnapshot] = useState<PlatformPermissionSnapshot | null>(
    userId != null ? cache.get(userId) ?? null : null,
  );
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (userId == null) {
      setSnapshot(null);
      return undefined;
    }
    const cached = cache.get(userId);
    if (cached) { setSnapshot(cached); return undefined; }
    let cancelled = false;
    loadPlatformPermissions(userId, typecompte).then((snap) => {
      if (!cancelled) setSnapshot(snap);
    });
    return () => { cancelled = true; };
  }, [userId, typecompte, tick]);

  const has = useCallback(
    (permission: string | string[] | null | undefined): boolean => {
      if (permission == null) return true;
      if (snapshot === null) return true; // évite un flash de blocage au chargement
      if (snapshot.isMaster) return true;
      const needed = Array.isArray(permission) ? permission : [permission];
      if (needed.length === 0) return true;
      return needed.some((p) => snapshot.permissions.includes(p));
    },
    [snapshot],
  );

  return {
    loading: snapshot === null,
    ready: snapshot !== null,
    isMaster: snapshot?.isMaster ?? false,
    isStaff: snapshot?.isStaff ?? false,
    roleLabel: snapshot?.roleLabel ?? '',
    permissions: snapshot?.permissions ?? [],
    has,
    refresh: () => { if (userId != null) cache.delete(userId); setTick((t) => t + 1); },
  };
}

export default usePlatformPermissions;